import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import { useDemoData } from '../components/useDemoData';
import BillDetectiveCrossLink from '../components/BillDetectiveCrossLink';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Calculator, DollarSign, TrendingUp, TrendingDown, Users,
  Layers, ArrowRight, BarChart3, Activity, Target,
  Code, Globe, ShoppingCart, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const DEMO_UNIT_ECON = {
  summary: {
    costPerCustomer: 12.40,
    prevCostPerCustomer: 14.80,
    costPerTransaction: 0.0034,
    costPerApiCall: 0.000042,
    grossMargin: 74.2,
    prevGrossMargin: 71.8,
    totalCustomers: 14200,
    totalTransactions: 8420000,
  },
  byProduct: [
    { product: 'Core Platform', customers: 14200, cloudCost: 42800, costPerUser: 3.01, revenue: 284000, margin: 84.9, trend: 'improving' },
    { product: 'API Gateway', calls: 124000000, cloudCost: 28600, costPer1M: 230.65, revenue: 86000, margin: 66.7, trend: 'stable' },
    { product: 'Data Pipeline', gbProcessed: 842, cloudCost: 18400, costPerGB: 21.85, revenue: 62000, margin: 70.3, trend: 'declining' },
    { product: 'AI Features', inferences: 2800000, cloudCost: 34200, costPer1K: 12.21, revenue: 98000, margin: 65.1, trend: 'improving' },
    { product: 'Storage / CDN', gbStored: 48200, cloudCost: 14800, costPerGB: 0.31, revenue: 42000, margin: 64.8, trend: 'stable' },
  ],
  byFeature: [
    { feature: 'User Authentication', dailyCost: 42, callsPerDay: 89000, costPer1K: 0.47, status: 'efficient' },
    { feature: 'Dashboard Rendering', dailyCost: 186, callsPerDay: 42000, costPer1K: 4.43, status: 'moderate' },
    { feature: 'Search / Query', dailyCost: 324, callsPerDay: 28000, costPer1K: 11.57, status: 'expensive' },
    { feature: 'Report Generation', dailyCost: 218, callsPerDay: 3200, costPer1K: 68.13, status: 'very-expensive' },
    { feature: 'File Upload/Process', dailyCost: 156, callsPerDay: 8400, costPer1K: 18.57, status: 'expensive' },
    { feature: 'Email Notifications', dailyCost: 28, callsPerDay: 15000, costPer1K: 1.87, status: 'efficient' },
    { feature: 'AI Chat / Inference', dailyCost: 412, callsPerDay: 12000, costPer1K: 34.33, status: 'very-expensive' },
    { feature: 'Background Jobs', dailyCost: 94, callsPerDay: 52000, costPer1K: 1.81, status: 'efficient' },
  ],
  customerSegments: [
    { segment: 'Enterprise (>$500/mo)', customers: 120, cloudCostPerUser: 28.40, revenuePerUser: 842, margin: 96.6 },
    { segment: 'Pro ($50-500/mo)', customers: 2400, cloudCostPerUser: 14.20, revenuePerUser: 124, margin: 88.5 },
    { segment: 'Starter ($20-50/mo)', customers: 4800, cloudCostPerUser: 8.60, revenuePerUser: 32, margin: 73.1 },
    { segment: 'Free Tier', customers: 6880, cloudCostPerUser: 4.20, revenuePerUser: 0, margin: -100 },
  ],
};

const UnitEconomics = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    // Iter84ak — real backend; fall back to rich local demo.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/unit-economics/snapshot`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) { setData(DEMO_UNIT_ECON); setLoading(false); }
      } catch (_e) {
        if (!cancelled) { setData(DEMO_UNIT_ECON); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  if (!shouldShowData) {
    return <EmptyDataState title="Unit Economics Dashboard" description="Know exactly what each customer, feature, and API call costs. Price your product right and optimize your gross margin." icon={Calculator} />;
  }

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const statusColor = (s) => ({ efficient: 'bg-emerald-500/10 text-emerald-600', moderate: 'bg-amber-500/10 text-amber-600', expensive: 'bg-orange-500/10 text-orange-600', 'very-expensive': 'bg-red-500/10 text-red-600' }[s]);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="unit-economics-page">
      <SEOHead
        title="Unit Economics — Cost per customer, per feature, per API call"
        description="The clearest unit-economics dashboard for SaaS: $/customer, $/feature, $/API call. Connect Stripe + your cloud bill and see margins in 30 seconds."
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500 bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            Unit Economics Dashboard
          </h1>
          <p className="text-sm text-slate-400 dark:text-slate-400 mt-1">
            <span className="font-medium">Cost per customer, transaction, API call.</span> Built for Engineering &amp; Product. (For per-customer MRR/profitability, see <a href="/customer-pl" className="text-emerald-600 hover:underline font-medium">Customer P&amp;L</a>.)
          </p>
        </div>
      </div>

      <BillDetectiveCrossLink
        testid="ue-bd-crosslink"
        message="Unit Economics gives broad snapshots. For per-engineer / per-commit attribution →"
      />

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4">
          <p className="text-xs text-emerald-600 mb-1">Cost per Customer</p>
          <p className="text-2xl font-bold text-emerald-700">${data.summary.costPerCustomer}</p>
          <p className="text-xs text-emerald-500 flex items-center gap-1"><TrendingDown className="w-3 h-3" />-${(data.summary.prevCostPerCustomer - data.summary.costPerCustomer).toFixed(2)} vs last month</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Cost per Transaction</p>
          <p className="text-2xl font-bold text-slate-900">${data.summary.costPerTransaction}</p>
          <p className="text-xs text-slate-400">{(data.summary.totalTransactions / 1000000).toFixed(1)}M transactions/mo</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Cost per API Call</p>
          <p className="text-2xl font-bold text-slate-900">${data.summary.costPerApiCall}</p>
          <p className="text-xs text-slate-400">across all endpoints</p>
        </CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4">
          <p className="text-xs text-emerald-600 mb-1">Gross Margin</p>
          <p className="text-2xl font-bold text-emerald-700">{data.summary.grossMargin}%</p>
          <p className="text-xs text-emerald-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" />+{(data.summary.grossMargin - data.summary.prevGrossMargin).toFixed(1)}% MoM</p>
        </CardContent></Card>
      </div>

      {/* By Product */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-emerald-500" />Cost by Product Line</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 text-slate-400 font-medium">Product</th>
                <th className="text-right py-2 text-slate-400 font-medium">Cloud Cost</th>
                <th className="text-right py-2 text-slate-400 font-medium">Unit Cost</th>
                <th className="text-right py-2 text-slate-400 font-medium">Revenue</th>
                <th className="text-right py-2 text-slate-400 font-medium">Margin</th>
                <th className="text-right py-2 text-slate-400 font-medium">Trend</th>
              </tr></thead>
              <tbody>
                {data.byProduct.map((p, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 font-medium text-slate-800">{p.product}</td>
                    <td className="py-3 text-right font-mono">${p.cloudCost.toLocaleString()}</td>
                    <td className="py-3 text-right font-mono text-slate-400">
                      {p.costPerUser ? `$${p.costPerUser}/user` : p.costPer1M ? `$${p.costPer1M}/1M` : p.costPerGB ? `$${p.costPerGB}/GB` : `$${p.costPer1K}/1K`}
                    </td>
                    <td className="py-3 text-right font-mono">${p.revenue.toLocaleString()}</td>
                    <td className="py-3 text-right"><span className={`font-bold ${p.margin > 80 ? 'text-emerald-600' : p.margin > 60 ? 'text-emerald-600' : 'text-amber-600'}`}>{p.margin}%</span></td>
                    <td className="py-3 text-right">
                      <Badge className={`text-[11px] ${p.trend === 'improving' ? 'bg-emerald-500/10 text-emerald-600' : p.trend === 'declining' ? 'bg-red-500/10 text-red-600' : 'bg-slate-100 text-slate-400'}`}>{p.trend}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* By Feature + By Customer Segment */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Code className="w-5 h-5 text-emerald-500" />Cost per Feature</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.byFeature.map((f, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-800">{f.feature}</p>
                  <p className="text-xs text-slate-400">{f.callsPerDay.toLocaleString()} calls/day</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono font-bold text-slate-800">${f.costPer1K}/1K</span>
                  <Badge className={`${statusColor(f.status)} text-[11px] border-0`}>{f.status}</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-amber-500" />Cost by Customer Segment</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data.customerSegments.map((seg, i) => (
              <div key={i} className={`p-4 rounded-xl border ${seg.margin < 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-slate-800">{seg.segment}</p>
                  <span className="text-xs text-slate-400">{seg.customers.toLocaleString()} customers</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-400">Cloud: <span className="font-mono font-medium text-slate-700">${seg.cloudCostPerUser}/user</span></div>
                  <div className="text-xs text-slate-400">Revenue: <span className="font-mono font-medium text-slate-700">${seg.revenuePerUser}/user</span></div>
                  <span className={`text-sm font-bold ${seg.margin > 80 ? 'text-emerald-600' : seg.margin > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {seg.margin > 0 ? `${seg.margin}%` : 'Negative'}
                  </span>
                </div>
              </div>
            ))}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-amber-700"><Zap className="w-3 h-3 inline mr-1" />Free tier costs $28,896/mo. Convert 5% to Starter to break even.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UnitEconomics;
